import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {ReplaySubject, Subject} from 'rxjs';
import {FormControl} from '@angular/forms';
import {UtilService} from '../../services/util/util.service';
import {takeUntil} from 'rxjs/operators';
import {sortBy} from 'lodash-es';
import {MatCheckboxChange} from '@angular/material/checkbox';

@Component({
  selector: 'app-selection-card',
  templateUrl: './selection-card.component.html',
  styleUrl: './selection-card.component.scss'
})
export class SelectionCardComponent implements OnInit, OnDestroy {
  @ViewChild('selectionContent', { read: ElementRef, static: true }) selectionContent: ElementRef;
  @ViewChild('searchInput', { read: ElementRef, static: true }) searchInput: ElementRef;

  @Input() public title: string;
  @Input() public expandable: boolean;
  @Input() public withSearch: boolean;
  @Input() public itemList: any[];
  @Input() public filterBy: string[] = [];
  @Input() public placeholder: string = 'general.api_select_search';
  @Input() public itemTemplate: TemplateRef<any>;
  @Input() public selectionKey: string;

  public searchControl: FormControl<string> = new FormControl<string>('');
  public itemListFiltered: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public cardExpanded: boolean;
  public noListValues: boolean;
  private _onDestroy = new Subject<void>();

  private _selectedValues: any[] = [];
  @Input() public set selectedValues(values: any[]) {
    this._selectedValues = values;
    this.selectedValuesChange.emit(this._selectedValues);
  }
  public get selectedValues(): any[] {
    return this._selectedValues;
  }
  @Output() public selectedValuesChange: EventEmitter<any[]> = new EventEmitter<any[]>();

  public ngOnInit(): void {
    this.filter();
    this.searchControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => this.filter());
    this.itemListFiltered.pipe(takeUntil(this._onDestroy)).subscribe(list => this.noListValues = !list?.length);
  }
  public ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public toggleExpanded(): void {
    this.cardExpanded = !this.cardExpanded;
    if (!this.cardExpanded) {
      this.clearSearch();
    }
  }

  public clearSearch(focus?: boolean): void {
    this.searchControl.setValue('');
    if (focus) {
      this.focusSearchField();
    }
  }

  public toggleItem(checkboxChange: MatCheckboxChange, value: any): void {
    if (checkboxChange.checked) {
      this.selectedValues = [...this.selectedValues, this.selectionKey ? value[this.selectionKey] : value];
    } else {
      this.selectedValues = this.selectedValues.filter(v => v !== (this.selectionKey ? value[this.selectionKey] : value));
    }
  }
  public isItemSelected(value: any): boolean {
    return this.selectedValues.includes(this.selectionKey ? value[this.selectionKey] : value);
  }

  public toggleAll(checkboxChange: MatCheckboxChange): void {
    if (checkboxChange.checked) {
      this.selectedValues = this.itemList.map(value => this.selectionKey ? value[this.selectionKey] : value);
    } else {
      this.selectedValues = [];
    }
  }
  public areAllSelected(): boolean {
    return this.selectedValues.length === this.itemList.length;
  }
  public areSomeSelected(): boolean {
    return !!this.selectedValues.length && !this.areAllSelected();
  }

  public filter(): void {
    if (!this.itemList?.length) {
      return;
    }
    const sortedValues: any[] = this.sort(this.itemList, this.filterBy);
    let searchValue: string = this.searchControl.value;
    if (!searchValue) {
      this.itemListFiltered.next(sortedValues.slice());
      return;
    } else {
      searchValue = searchValue.toLowerCase();
    }
    this.updateFilteredItemList(sortedValues.filter(value => {
      if (this.filterBy?.length) {
        return this.filterBy.filter(f =>  {
          return f.split('.').reduce((prev, cur) => {
            if (typeof(prev[cur]) === 'number') {
              prev[cur] = prev[cur].toString();
            }
            return prev[cur];
          }, value)?.toLowerCase().includes(searchValue)
        }).length;
      }
      if (typeof(value) === 'number') {
        value = value.toString();
      }
      return value.toLowerCase().includes(searchValue);
    }));
  }
  private sort(values: any[], filterBy: string[]): any[] {
    return sortBy(values, filterBy, filterBy.map(_ => 'asc'));
  }
  private updateFilteredItemList(filteredItems: any[]): void {
    this.itemListFiltered.next(filteredItems.slice());
  }

  public handleKeyDown(event: KeyboardEvent): void {
    if ((event.key && event.key.length === 1) ||
      (event.code >= UtilService.CODE_A && event.code <= UtilService.CODE_Z) ||
      (event.code >= UtilService.CODE_0 && event.code <= UtilService.CODE_9) ||
      (event.key === UtilService.KEY_SPACE)
    ) {
      event.stopPropagation();
    }
    if (event.key && event.key === UtilService.KEY_ENTER) {
      setTimeout(() => this.focusSearchField());
    }
  }
  private focusSearchField(): void {
    if (!this.searchInput) { return; }
    const content = this.selectionContent.nativeElement;
    const scrollTop = content.scrollTop;
    this.searchInput.nativeElement.focus();
    content.scrollTop = scrollTop;
  }
}
